import React, { useEffect, useRef } from 'react';
import '../styles/Blob3D.scss';

function Blob3D() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const setCanvasSize = () => {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };

    setCanvasSize();
    container.appendChild(canvas);

    let animationId;
    let time = 0;

    const drawBlob = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Calculate floating position
      const floatX = Math.sin(time * 0.003) * (canvas.width * 0.25);
      const floatY = Math.cos(time * 0.002) * (canvas.height * 0.25);

      const centerX = canvas.width / 2 + floatX;
      const centerY = canvas.height / 2 + floatY;

      time += 1;

      // Draw outer glow
      const glowGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 200);
      glowGradient.addColorStop(0, 'rgba(16, 185, 129, 0.15)');
      glowGradient.addColorStop(0.5, 'rgba(249, 168, 212, 0.08)');
      glowGradient.addColorStop(1, 'rgba(249, 168, 212, 0)');

      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 200, 0, Math.PI * 2);
      ctx.fill();

      // Main blob gradient
      const gradient = ctx.createLinearGradient(
        centerX - 100,
        centerY - 100,
        centerX + 100,
        centerY + 100
      );

      gradient.addColorStop(0, '#10B981');
      gradient.addColorStop(0.5, '#F9A8D4');
      gradient.addColorStop(1, '#60A5FA');

      // Draw main blob with smooth waves
      ctx.beginPath();
      const points = 40;
      const baseRadius = 100;

      for (let i = 0; i < points; i++) {
        const angle = (i / points) * Math.PI * 2;

        // Create smooth wave motion
        const wave1 = Math.sin(angle * 3 + time * 0.01) * 15;
        const wave2 = Math.cos(angle * 2 - time * 0.015) * 10;
        const wave3 = Math.sin(angle + time * 0.008) * 8;

        const radius = baseRadius + wave1 + wave2 + wave3;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }

      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Add soft inner highlight
      const highlightGradient = ctx.createRadialGradient(
        centerX - 30,
        centerY - 40,
        0,
        centerX,
        centerY,
        150
      );

      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx.fillStyle = highlightGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 150, 0, Math.PI * 2);
      ctx.fill();

      animationId = requestAnimationFrame(drawBlob);
    };

    drawBlob();

    const handleResize = () => {
      setCanvasSize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
  }, []);

  return <div ref={containerRef} className="blob-3d-container" />;
}

export default Blob3D;
